import { useState } from 'react';

import IconClose from '@/assets/svg/icon-close-out.svg?react';
import IconDocx from '@/assets/svg/icon-docx.svg?react';
import IconMaximize from '@/assets/svg/icon-expand.svg?react';
import IconFile from '@/assets/svg/icon-file.svg?react';
import IconNewPage from '@/assets/svg/icon-img-new-page.svg?react';
import IconImageRotateDown from '@/assets/svg/icon-img-rotate-down.svg?react';
import IconImageRotateUp from '@/assets/svg/icon-img-rotate-up.svg?react';
import IconZoomIn from '@/assets/svg/icon-img-zoom-in.svg?react';
import IconZoomOut from '@/assets/svg/icon-img-zoom-out.svg?react';
import IconImg from '@/assets/svg/icon-img.svg?react';
import IconMp4 from '@/assets/svg/icon-mp4.svg?react';
import IconPdf from '@/assets/svg/icon-pdf.svg?react';
import IconTxt from '@/assets/svg/icon-txt.svg?react';

import { cn, getFileExtension } from '@/lib/utils';

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface ImageViewerProps {
  isPhotoViewerOpen: boolean;
  setIsPhotoViewerOpen: (value: boolean) => void;
  imageFile?: File;
}
interface ImageTransform {
  scale: number;
  rotation: number;
}

const ImageViewerWidget = ({
  isPhotoViewerOpen,
  imageFile,
  setIsPhotoViewerOpen,
}: ImageViewerProps) => {
  const [transform, setTransform] = useState<ImageTransform>({
    scale: 1,
    rotation: 0,
  });
  const [isMaximized, setIsMaximized] = useState(false);
  const imageUrl = imageFile ? URL.createObjectURL(imageFile) : '';

  const handleMaximize = () => {
    setIsMaximized((prev) => !prev);
  };

  const handleZoom = (direction: 'in' | 'out') => {
    setTransform((prev) => ({
      ...prev,
      scale:
        direction === 'in'
          ? Math.min(prev.scale + 0.25, 3)
          : Math.max(prev.scale - 0.25, 0.5),
    }));
  };

  const handleRotate = (direction: 'up' | 'down') => {
    setTransform((prev) => ({
      ...prev,
      rotation: direction === 'up' ? prev.rotation + 90 : prev.rotation - 90,
    }));
  };

  const renderPreview = () => {
    const extension = getFileExtension(imageFile!);
    switch (extension) {
      case 'jpg':
      case 'jpeg':
      case 'png':
        return (
          <img
            src={imageUrl}
            alt={imageFile?.name}
            className="h-full max-h-[90vh] w-full max-w-full overflow-hidden rounded-md object-contain transition-transform duration-200"
            style={{
              transform: `scale(${transform.scale}) rotate(${transform.rotation}deg)`,
            }}
          />
        );

      case 'heic':
      case 'heif':
        return (
          <div className="flex h-full w-full flex-col items-center justify-center rounded-md bg-gray-200">
            <IconImg className="h-[200px] w-[200px]" />
            <p className="text-lg text-primary">{imageFile?.name}</p>
          </div>
        );

      case 'mov':
      case 'mp4':
        return (
          <div className="flex h-full w-full flex-col items-center justify-center rounded-md bg-gray-200">
            <IconMp4 className="h-[200px] w-[200px] fill-dark-red" />
            <p className="text-lg text-primary">{imageFile?.name}</p>
          </div>
        );
      case 'txt':
        return (
          <div className="flex h-full w-full flex-col items-center justify-center rounded-md bg-gray-200">
            <IconTxt className="h-[200px] w-[200px]" />
            <p className="text-lg text-primary">{imageFile?.name}</p>
          </div>
        );

      case 'doc':
      case 'docx':
        return (
          <div className="flex h-full w-full flex-col items-center justify-center rounded-md bg-gray-200">
            <IconDocx className="h-[200px] w-[200px]" />
            <p className="text-lg text-primary">{imageFile?.name}</p>
          </div>
        );
      case 'pdf':
        return (
          <div className="flex h-full w-full flex-col items-center justify-center rounded-md bg-gray-200">
            <IconPdf className="h-[200px] w-[200px] fill-dark-red" />
            <p className="text-lg text-primary  ">{imageFile?.name}</p>
          </div>
        );

      default:
        return (
          <div className="flex h-full w-full flex-col items-center justify-center rounded-md bg-gray-200">
            <IconFile className="h-[200px] w-[200px] fill-dark-red" />
            <p className="text-lg text-primary  ">{imageFile?.name}</p>
          </div>
        );
    }
  };

  return (
    <Dialog open={isPhotoViewerOpen} onOpenChange={setIsPhotoViewerOpen}>
      <DialogContent
        className={cn(
          'flex items-center justify-center border-none bg-transparent p-0 [&_#dialog-close]:hidden',
          isMaximized
            ? 'h-screen w-screen max-w-none'
            : 'max-h-[90vh] max-w-[900px]',
        )}
      >
        <DialogTitle className="mb-3 text-2xl font-semibold" hidden>
          Фотографія
        </DialogTitle>
        <div
          className={cn(
            'relative flex items-center justify-center',
            isMaximized ? 'h-screen w-screen' : 'sm:h-[700px] sm:w-[900px]',
          )}
        >
          <DialogClose className="absolute right-5 top-5 z-50 transition-colors hover:[&_svg]:fill-primary">
            <IconClose className="h-[20px] w-[20px] fill-white transition-colors" />
          </DialogClose>
          {imageFile?.type.startsWith('image/') && (
            <button
              className="absolute right-14 top-5 z-50 transition-colors hover:[&_svg]:fill-primary"
              onClick={handleMaximize}
            >
              <IconMaximize className="h-[20px] w-[20px] fill-white transition-colors" />
            </button>
          )}

          {/* Main image */}
          {renderPreview()}

          {/* Controls toolbar if image */}
          {imageFile?.type.startsWith('image/') && (
            <div className="absolute bottom-[-60px] left-1/2 flex h-[50px] w-[400px] -translate-x-1/2 items-center justify-center gap-14 rounded-md bg-[#002C4180] p-2 backdrop-blur-sm sm:bottom-[28px] sm:h-[60px]">
              <div className="flex items-center">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      className="rounded-sm p-2 text-white hover:bg-white/20"
                      onClick={() => handleRotate('up')}
                    >
                      <IconImageRotateUp className="h-5 w-5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Повернути за годинниковою стрілкою</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      className="rounded-sm p-2 text-white hover:bg-white/20"
                      onClick={() => handleRotate('down')}
                    >
                      <IconImageRotateDown className="h-5 w-5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Повернути проти годинникової стрілки</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex items-center">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      className="rounded-sm p-2 text-white hover:bg-white/20"
                      onClick={() => handleZoom('in')}
                    >
                      <IconZoomIn className="h-5 w-5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Збільшити</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      className="rounded-sm p-2 text-white hover:bg-white/20"
                      onClick={() => handleZoom('out')}
                    >
                      <IconZoomOut className="h-5 w-5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Зменшити</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex items-center">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      className="rounded-sm p-2 text-white hover:bg-white/20"
                      onClick={() => window.open(imageUrl, '_blank')}
                    >
                      <IconNewPage className="h-5 w-5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Відкрити в новій вкладці</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
      <DialogDescription className="text-base text-primary" hidden>
        {imageFile?.name}
      </DialogDescription>
    </Dialog>
  );
};

export default ImageViewerWidget;
